import {
  Importance,
  RiskSignalTag,
  RiskSignalTags,
  SignalKind
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  ClientHealthBand,
  ClientHealthRange,
  GetClientHealthSignalsParams
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { RISK_TYPE_OPTIONS } from '@src/Pages/ActivityFeed/Filters/activity-feed-filters-constants';
import {
  Danger200,
  Danger500,
  Danger700,
  Success200,
  Success500,
  Success700
} from '@src/utils/colors';

export const CLIENT_HEALTH_COUNTS_QUERY_KEY = 'client-health-counts';
export const CLIENT_HEALTH_SIGNALS_QUERY_KEY = 'client-health-signals';

// Distance from the list's end, in px, at which the next page starts loading.
export const CLIENT_HEALTH_LOAD_MORE_THRESHOLD = 120;

export const DEFAULT_CLIENT_HEALTH_RANGE = ClientHealthRange.LAST_90;

export const CLIENT_HEALTH_RANGE_OPTIONS = [
  { value: ClientHealthRange.LAST_7, label: 'Past 7 days' },
  { value: ClientHealthRange.LAST_30, label: 'Past 30 days' },
  { value: ClientHealthRange.LAST_90, label: 'Past 90 days' },
  { value: ClientHealthRange.LAST_365, label: 'Past Year' }
];

export const BAND_LABELS: Record<ClientHealthBand, string> = {
  [Importance.HIGH]: 'High',
  [Importance.MEDIUM]: 'Med',
  [Importance.LOW]: 'Low'
};

export const RISK_FILTER_ALL = 'all' as const;
export type RiskFilterType = typeof RISK_FILTER_ALL | RiskSignalTag;

export const RISK_FILTER_OPTIONS = [
  { value: RISK_FILTER_ALL, label: 'All' },
  ...RiskSignalTags.map(tag => ({
    value: tag,
    label: RISK_TYPE_OPTIONS.find(option => option.value === tag)?.label ?? tag
  }))
];

export interface SignalSelection {
  signal: SignalKind;
  band: ClientHealthBand;
}

export type ClientHealthSignalsFilter = Pick<
  GetClientHealthSignalsParams,
  'importance' | 'signalTag'
>;

export interface WeeklySignalRow {
  label: string;
  positiveHigh: number;
  positiveMedium: number;
  positiveLow: number;
  riskHigh: number;
  riskMedium: number;
  riskLow: number;
  total: number;
}

export const CLIENT_HEALTH_SERIES = [
  {
    dataKey: 'riskHigh',
    signal: SignalKind.RISK,
    band: Importance.HIGH,
    label: 'Risk: High',
    color: Danger700
  },
  {
    dataKey: 'riskMedium',
    signal: SignalKind.RISK,
    band: Importance.MEDIUM,
    label: 'Risk: Med',
    color: Danger500
  },
  {
    dataKey: 'riskLow',
    signal: SignalKind.RISK,
    band: Importance.LOW,
    label: 'Risk: Low',
    color: Danger200
  },
  {
    dataKey: 'positiveLow',
    signal: SignalKind.POSITIVE,
    band: Importance.LOW,
    label: 'Positive: Low',
    color: Success200
  },
  {
    dataKey: 'positiveMedium',
    signal: SignalKind.POSITIVE,
    band: Importance.MEDIUM,
    label: 'Positive: Med',
    color: Success500
  },
  {
    dataKey: 'positiveHigh',
    signal: SignalKind.POSITIVE,
    band: Importance.HIGH,
    label: 'Positive: High',
    color: Success700
  }
] as const satisfies readonly {
  dataKey: keyof WeeklySignalRow;
  signal: SignalKind;
  band: ClientHealthBand;
  label: string;
  color: string;
}[];
export type ClientHealthSeries = (typeof CLIENT_HEALTH_SERIES)[number];
